/**
 * Fassungen der Grundstein-Bausteine (Ticket 1.241, Change „Entwürfe ins ERP“ §4.3) als reine Klassen-Logik: je
 * Fassung genau eine Klassenliste aus Tokens (styles.css), damit Vitest jede Fassung prüfen kann und die Bausteine
 * selbst nur noch zusammensetzen. Keine Fachlogik: welche Stufe oder welcher Zustand gilt, entscheiden die Seiten.
 */
import { pillToneClass, type PillTone } from './pill-tone.ts';

export type ButtonVariant = 'primary' | 'secondary' | 'danger';
export type ButtonSize = 'md' | 'sm';

/**
 * Knopf (Redesign OpenAI-Stil 31.10.2026): Pille, Schrift 500; `md` 44 px (bündig mit Feldern), `sm` 38 px (`--button-h`), bei Touch 44 px
 * (`--control-h`). Hauptknopf TILL-Dunkelblau, Zweitknopf ohne Fläche mit feinem Rand `line` (Maus darüber `hover`).
 * Beim Drücken gibt der Knopf leicht nach (98 %, 150 ms; bei „Bewegung reduzieren“ ohne Übergang).
 */
export function buttonClass(variant: ButtonVariant, size: ButtonSize = 'md'): string {
  const base =
    'inline-flex cursor-pointer items-center justify-center gap-space-2 rounded-full font-medium whitespace-nowrap transition-[opacity,background-color,border-color,transform] duration-[var(--motion-fast)] active:enabled:scale-[0.98] disabled:cursor-default disabled:opacity-[var(--opacity-half)]';
  const sizeClass = size === 'sm' ? 'h-button-h px-space-4 text-md pointer-coarse:h-control-h' : 'h-control-h px-button-pad-x text-row';
  const look =
    variant === 'primary'
      ? 'border border-transparent bg-primary text-primary-foreground hover:enabled:bg-brand-hover'
      : variant === 'danger'
        ? 'border border-transparent bg-danger text-on-danger hover:enabled:opacity-[var(--opacity-firm-plus)]'
        : 'border border-line bg-transparent text-ink hover:enabled:bg-hover';
  return `${base} ${sizeClass} ${look}`;
}

export interface FieldLook {
  /** Fehler: 2 px Rand `clarify-fg` (1 px Rand + 1 px Innenschatten), Fläche bleibt weiß. */
  invalid?: boolean | undefined;
  /** Gesperrt (nur lesen, nicht änderbar): keine Fläche, Rand `line`, Schrift `ink-2`, kein Hover. */
  locked?: boolean | undefined;
}

/**
 * Eingabefeld (Baustein A, Ticket 1.256; Aussehen Rudolf 21.10.2026, DESIGN „Eingabefeld“): eine Klassenliste für alle
 * Feldzustände von `Input`, `Textarea`, `Select` und Datumsfeld. Leer und mit Wert: weiße Fläche `card`, Rand
 * `field-line` 1 px (≥ 3 : 1 gegen Weiß), Platzhalter `ink-2`; Hover Rand `field-line-hover` (eine Stufe dunkler, nicht im
 * Fokus); Fokus (auch nach Mausklick, daher `focus:` statt `focus-visible:`) 2 px Rand `focus-ring` ohne Layoutsprung
 * (1 px Rand + 1 px Innenschatten); Fehler 2 px `clarify-fg` (auch im Fokus); gesperrt ohne Fläche, Rand `line`.
 * Felder in einem gesperrten `fieldset` (Lesemodus) sehen über `disabled:` wie gesperrt aus. Höhe, Abstände und
 * Breite setzt jeder Baustein selbst.
 */
export function fieldClass({ invalid = false, locked = false }: FieldLook = {}): string {
  const base = 'rounded-field border text-row placeholder:text-ink-2 transition-[background-color,border-color,box-shadow]';
  if (locked) return `${base} cursor-default border-line bg-transparent text-ink-2`;
  const edit =
    'bg-card text-ink disabled:cursor-default disabled:border-line disabled:bg-transparent disabled:text-ink-2 disabled:shadow-none';
  // Fehler bleibt auch im Fokus und unter der Maus sichtbar (roter Rand); nur der normale Rand wird beim Hover dunkler.
  const state = invalid
    ? 'border-clarify shadow-field-error focus:border-clarify focus:shadow-field-error'
    : 'border-field-line hover:enabled:not-focus:border-field-line-hover focus:border-ring focus:shadow-field-focus';
  return `${base} ${edit} ${state}`;
}

/**
 * Abzeichen-Töne: die fünf Töne des Ton-Vertrags (`pill-tone.ts`, Story 26.17) plus Betriebsschalter-Namen
 * (`live` = ready, `probe` = check, `aus` = grau ausgeschaltet) und `brand` (= info, ein Blau im ganzen ERP).
 */
export type BadgeTone = PillTone | 'live' | 'probe' | 'aus' | 'brand';

const BADGE_TONE: Readonly<Record<Exclude<BadgeTone, 'aus'>, PillTone>> = {
  ready: 'ready',
  notice: 'notice',
  check: 'check',
  escalate: 'escalate',
  clarify: 'clarify',
  critical: 'critical',
  info: 'info',
  neutral: 'neutral',
  live: 'ready',
  probe: 'check',
  brand: 'info',
};

/** Abzeichen: eine Höhe (`--pill-h`), Pille, kleine Schrift; Farbe nur über den Ton-Vertrag. */
export function badgeClass(tone: BadgeTone): string {
  const base = 'inline-flex h-pill-h flex-none items-center gap-space-1 rounded-pill px-space-3 text-pill font-medium whitespace-nowrap';
  return `${base} ${tone === 'aus' ? 'bg-fill-off text-ink-2' : pillToneClass(BADGE_TONE[tone])}`;
}

/**
 * Mahnstufen (nur Darstellung; die Stufen-Logik baut Epic 15): vier Stufen plus „gesperrt“ grau (DESIGN
 * Mahnstufen-Abzeichen). Abnahme durch Steuerberater/Rudolf nötig.
 */
export const DUNNING_LEVELS = ['reminder', 'first', 'second', 'collection', 'blocked'] as const;
export type DunningLevel = (typeof DUNNING_LEVELS)[number];

export const DUNNING_LABELS: Readonly<Record<DunningLevel, string>> = {
  reminder: 'Zahlungserinnerung',
  first: '1. Mahnung',
  second: '2. Mahnung',
  collection: 'Inkasso',
  blocked: 'gesperrt',
};

/**
 * Mahnstufen im Ton-Vertrag (Story 26.17, Stufenleiter [RUDOLF] 01.11.2026): Zahlungserinnerung bernstein,
 * 1. Mahnung orange, 2. Mahnung rot, Inkasso dunkelrot, gesperrt grau. Die Stufe steht immer im Wort.
 * Abnahme durch Steuerberater/Rudolf nötig (Mahnwesen-Darstellung).
 */
export const DUNNING_TONE: Readonly<Record<DunningLevel, PillTone>> = {
  reminder: 'check',
  first: 'escalate',
  second: 'clarify',
  collection: 'critical',
  blocked: 'neutral',
};

export function dunningClass(level: DunningLevel): string {
  return `inline-flex h-pill-h flex-none items-center rounded-pill px-space-3 text-pill font-medium whitespace-nowrap ${pillToneClass(DUNNING_TONE[level])}`;
}

/** Zustände einer Ampel-Kachel (C01 `.ax`). */
export const SIGNAL_STATES = ['ok', 'clarify', 'check', 'waiting'] as const;
export type SignalState = (typeof SIGNAL_STATES)[number];
export type SignalSize = 'md' | 'sm';

export const SIGNAL_STATE_LABELS: Readonly<Record<SignalState, string>> = {
  ok: 'in Ordnung',
  clarify: 'klären',
  check: 'prüfen',
  waiting: 'wartet',
};

/**
 * Symbolgröße je Kachelgröße als Token-Klasse (24 px Kachel, 16 px Symbol `--signal-glyph`, beide Größen gleich; Ticket 1.242/1.271).
 * Die Klasse übersteuert `width`/`height` des lucide-Symbols.
 */
export function signalGlyphClass(_size: SignalSize = 'md'): string {
  return 'size-signal-glyph';
}

/** Ampel-Kachel: 24 px Radius 8 (klein ebenso, Rudolf 19.10.2026); Farbe je Zustand, Ring bei klären, prüfen und wartet. */
export function signalTileClass(state: SignalState, _size: SignalSize = 'md'): string {
  const sizeClass = 'size-signal-size rounded-mark';
  const look: Record<SignalState, string> = {
    ok: 'bg-tile text-signal-green',
    clarify: 'bg-clarify-bg text-clarify shadow-ring-clarify',
    check: 'bg-check-bg text-check shadow-ring-check',
    waiting: 'bg-fill text-signal-wait shadow-ring-wait',
  };
  return `relative inline-grid flex-none place-items-center ${sizeClass} ${look[state]}`;
}

/**
 * Seitenkopf und Datensatz-Kopf: höchstens 3 Knöpfe, der Rest wandert ins „⋯“. `reserved` = Plätze, die schon belegt
 * sind (Hauptknopf im Kopf, „Bearbeiten“). Ergebnis: sichtbare Zweitknöpfe und die für „⋯“.
 */
export const PAGE_HEAD_MAX_BUTTONS = 3;
export function pageHeadSplit<T>(reserved: number, secondary: readonly T[]): { shown: T[]; overflow: T[] } {
  const room = Math.max(0, PAGE_HEAD_MAX_BUTTONS - reserved);
  return { shown: secondary.slice(0, room), overflow: secondary.slice(room) };
}
